import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { API_BASE, authFetch } from '@/services/api';
import { getToken, setToken as persistToken, clearToken } from '@/constants/auth';

export interface AuthUser {
  id: number;
  username: string;
  email: string;
}

interface AuthState {
  user: AuthUser | null;
  token: string | null;
  needsPasswordSet: boolean;
  loading: boolean;
}

interface AuthContextValue extends AuthState {
  login: (username: string, password: string) => Promise<{ needsPasswordSet: boolean }>;
  logout: () => Promise<void>;
  setPassword: (currentPassword: string, newPassword: string, confirmPassword: string) => Promise<void>;
  setNeedsPasswordSet: (value: boolean) => void;
  checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setTokenState] = useState<string | null>(() => getToken());
  const [needsPasswordSet, setNeedsPasswordSet] = useState(false);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const t = getToken();
    if (!t) {
      setUser(null);
      setTokenState(null);
      setNeedsPasswordSet(false);
      setLoading(false);
      return;
    }
    try {
      const res = await authFetch(API_BASE + 'auth/me');
      const json = await res.json();
      if (!res.ok || !json.success) {
        clearToken();
        setTokenState(null);
        setUser(null);
        setNeedsPasswordSet(false);
        setLoading(false);
        return;
      }
      const data = json.data as { user: AuthUser; needs_password_set: boolean };
      setUser(data.user);
      setNeedsPasswordSet(!!data.needs_password_set);
    } catch {
      clearToken();
      setTokenState(null);
      setUser(null);
      setNeedsPasswordSet(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (username: string, password: string) => {
    const res = await fetch(API_BASE + 'auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: username.trim(), password }),
    });
    const json = await res.json();
    if (!res.ok || !json.success) throw new Error(json?.message || 'Login failed');
    const data = json.data as { user: AuthUser; token: string; needs_password_set: boolean };
    persistToken(data.token);
    setTokenState(data.token);
    setUser(data.user);
    setNeedsPasswordSet(!!data.needs_password_set);
    if (typeof window !== 'undefined') window.localStorage.setItem('profileUserId', String(data.user.id));
    return { needsPasswordSet: !!data.needs_password_set };
  }, []);

  const logout = useCallback(async () => {
    try {
      await authFetch(API_BASE + 'auth/logout', { method: 'POST' });
    } catch {
      // ignore
    }
    clearToken();
    setTokenState(null);
    setUser(null);
    setNeedsPasswordSet(false);
    if (typeof window !== 'undefined') window.localStorage.removeItem('profileUserId');
  }, []);

  const setPassword = useCallback(
    async (currentPassword: string, newPassword: string, confirmPassword: string) => {
      const res = await authFetch(API_BASE + 'auth/set-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
          confirm_password: confirmPassword,
        }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json?.message || 'Failed to set password');
      setNeedsPasswordSet(false);
    },
    []
  );

  const value: AuthContextValue = {
    user,
    token,
    needsPasswordSet,
    loading,
    login,
    logout,
    setPassword,
    setNeedsPasswordSet,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
